import { BLOCKS_RELOAD_DOCUMENT_EVENT } from "@pathwright/blocks-engine"
import { exhaustiveGuard } from "@pathwright/ux/src/utils/typing"
import { useCallback, useEffect, useMemo, useState } from "react"
import {
  FeatureAction,
  FeatureContextInput,
  FeatureKey,
  FeaturePermission,
  useContextualFeaturePermissionsQuery,
  useCreateContextualFeaturePermissionMutation,
  useUpdateContextualFeaturePermissionMutation,
  useDeleteContextualFeaturePermissionMutationMutation
} from "@pathwright/graphql-client/src/generated"

function dispatchBlocksReloadDocument() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(BLOCKS_RELOAD_DOCUMENT_EVENT))
  }
}

const BLOCKS_ROLLOUT_FEATURE_KEY = FeatureKey.BlocksRollout
const FEATURE_ACTION_MIGRATION = FeatureAction.BlocksMigration
const FEATURE_ACTION_DB_CREATION = FeatureAction.BlocksDbCreation
const ENABLED_PERMISSION = FeaturePermission.Everyone

/** School-level context (resource_id, cohort_id, user_id all null). */
export const SCHOOL_CONTEXT: FeatureContextInput = {
  resource_id: null,
  cohort_id: null,
  user_id: null
}

export const getBlocksResourceContext = (
  resourceId: number
): FeatureContextInput => ({
  resource_id: resourceId,
  cohort_id: null,
  user_id: null
})

export type BlocksMigrationLevel =
  | 0 // Off
  | 1 // DB Creation only (new steps)
  | 2 // Migration (all steps)

type UseBlocksMigrationCFPsOptions = {
  skip?: boolean
}

/**
 * Reads and updates the Blocks rollout CFPs for one exact context.
 *
 * Resource-context queries can include inherited school rows, so the returned
 * state is deliberately filtered to the requested context. Call this once for
 * the school and once for the resource when both scopes should be considered.
 */
export default function useBlocksMigrationCFPs(
  context: FeatureContextInput | null,
  { skip = false }: UseBlocksMigrationCFPsOptions = {}
) {
  const [isMutating, setIsMutating] = useState(false)

  const cfpsQuery = useContextualFeaturePermissionsQuery({
    skip: !context || skip,
    variables: {
      featureKeys: { in: [BLOCKS_ROLLOUT_FEATURE_KEY] },
      context: context ?? undefined
    }
  })

  const [createContextualFeaturePermission] =
    useCreateContextualFeaturePermissionMutation()
  const [updateContextualFeaturePermission] =
    useUpdateContextualFeaturePermissionMutation()
  const [deleteContextualFeaturePermission] =
    useDeleteContextualFeaturePermissionMutationMutation()

  const rawCfps = cfpsQuery.data?.contextualFeaturePermissions ?? []

  const cfps = useMemo(() => {
    if (!context) return []
    const scopeResourceId = context.resource_id ?? null
    return rawCfps.filter((cfp) => cfp.resource_id === scopeResourceId)
  }, [rawCfps, context])

  const migrationEnabled = useMemo(
    () =>
      !!cfps.find(
        (cfp) =>
          cfp.feature_action === FEATURE_ACTION_MIGRATION &&
          cfp.permission_in_context === ENABLED_PERMISSION
      ),
    [cfps]
  )

  const dbCreationEnabled = useMemo(
    () =>
      !!cfps.find(
        (cfp) =>
          cfp.feature_action === FEATURE_ACTION_DB_CREATION &&
          cfp.permission_in_context === ENABLED_PERMISSION
      ),
    [cfps]
  )

  // 0 = off, 1 = DB Creation only (new steps), 2 = Migration (all steps)
  const serverLevel = useMemo(
    (): BlocksMigrationLevel =>
      migrationEnabled ? 2 : dbCreationEnabled ? 1 : 0,
    [migrationEnabled, dbCreationEnabled]
  )

  const [level, setLevelLocal] = useState<BlocksMigrationLevel>(serverLevel)
  useEffect(() => {
    setLevelLocal(serverLevel)
  }, [serverLevel])

  const setLevel = useCallback(
    async (
      value: BlocksMigrationLevel,
      options?: {
        persist?: boolean
        mustMatchServer?: boolean
        // Enable the migration CFP without deleting an existing DB-creation CFP.
        // The default behavior (deleting the DB creation CFP) would cause a server error for Teachers/Moderators,
        // who are not allowed to delete CFPs.
        // Both CFPs can exist simultaneously, and the "migration" CFP will supersede the DB creation CFP.
        preserveDbCreation?: boolean
      }
    ) => {
      let { persist, mustMatchServer } = options ?? {}
      persist ??= true
      mustMatchServer ??= false
      setLevelLocal(value)
      if (!persist) return
      if (!context || skip || isMutating) return

      // Some users may not have permission to migrate blocks, so we throw an error if they try to set a level that doesn't match the server level.
      if (mustMatchServer && value !== serverLevel) {
        throw new Error(
          `Value ${value} does not match server level ${serverLevel}`
        )
      }

      // If the value matches the server level, we don't need to do anything.
      if (value === serverLevel) {
        return
      }

      setIsMutating(true)
      try {
        let requireRefetch = false

        async function deleteBlocksRolloutCFP(featureAction: FeatureAction) {
          if (cfps.find((cfp) => cfp.feature_action === featureAction)?.id) {
            requireRefetch = true
            await deleteContextualFeaturePermission({
              variables: {
                featureKey: BLOCKS_ROLLOUT_FEATURE_KEY,
                featureAction,
                context
              }
            })
          }
        }

        async function createBlocksRolloutCFP(featureAction: FeatureAction) {
          const existing = cfps.find(
            (cfp) => cfp.feature_action === featureAction
          )
          if (
            !cfps.find(
              (cfp) =>
                cfp.feature_action === featureAction &&
                cfp.permission_in_context === ENABLED_PERMISSION
            )?.id
          ) {
            requireRefetch = true
            const enablePermission = existing?.id
              ? updateContextualFeaturePermission
              : createContextualFeaturePermission
            await enablePermission({
              variables: {
                featureKey: BLOCKS_ROLLOUT_FEATURE_KEY,
                featureAction,
                permissionInContext: ENABLED_PERMISSION,
                context
              }
            })
          }
        }

        // If level is 0, we remove both CFPS.
        // Level 1 enables DB creation and removes migration.
        // Level 2 enables migration, optionally preserving DB creation.
        switch (value) {
          case 0:
            await Promise.all([
              deleteBlocksRolloutCFP(FeatureAction.BlocksMigration),
              deleteBlocksRolloutCFP(FeatureAction.BlocksDbCreation)
            ])
            break
          case 1:
            await Promise.all([
              deleteBlocksRolloutCFP(FeatureAction.BlocksMigration),
              createBlocksRolloutCFP(FeatureAction.BlocksDbCreation)
            ])
            break
          case 2:
            await Promise.all([
              options?.preserveDbCreation
                ? Promise.resolve()
                : deleteBlocksRolloutCFP(FeatureAction.BlocksDbCreation),
              createBlocksRolloutCFP(FeatureAction.BlocksMigration)
            ])
            break
          default:
            exhaustiveGuard(value)
        }

        if (requireRefetch) {
          await cfpsQuery.refetch()
          dispatchBlocksReloadDocument()
        }
      } catch (error) {
        setLevelLocal(serverLevel)
        throw error
      } finally {
        setIsMutating(false)
      }
    },
    [
      context,
      skip,
      isMutating,
      cfps,
      deleteContextualFeaturePermission,
      createContextualFeaturePermission,
      updateContextualFeaturePermission,
      cfpsQuery,
      serverLevel
    ]
  )

  return {
    error: cfpsQuery.error,
    level,
    migrationEnabled,
    setLevel,
    loading: cfpsQuery.loading || isMutating
  }
}
